// noinspection DuplicatedCode

import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { MatRippleModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { NgOptimizedImage } from '@angular/common';
import { TableElement, TableExportUtil, UnsubscribeOnDestroyAdapter } from '@shared';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { GeneralFunctionsService } from '@core/service/generalFunctions.service';
import { firstValueFrom, fromEvent, Subscription } from 'rxjs';
import { PatientHealthEventService } from '@core/http/table-data-services/patientHealthEvent.service';
import { PatientHealthEventDataSource } from '@core/data-source/PatientHealthEventDataSource';
import { PatientHealthEvent } from '@core/models/PatientHealthEvent';
import { PatientDetails } from '@core/models/PatientDetails';
import { CreateEditService } from '@core/http/create-edit-service';
import { AuthManagementService } from '@core/service/auth-management.service';
import { PatientHealthEventCreateEditComponent } from './patient-health-event-create-edit/patient-health-event-create-edit.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { TopWidgetsComponent } from '@shared/components/top-widgets/top-widgets.component';
import {
  PatientHealthEventDescriptionComponent
} from '../../catalogs/catalogs-list-v2/health-events/health-events-list/patient-health-event-description/patient-health-event-description.component';
import { ActivatedRoute, Router } from '@angular/router';
import { DoDeleteComponent } from '@shared/components/do-delete/do-delete.component';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';

import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { TutorService } from '@core/service/tutor.service';
import { UserService } from '@core/http/table-data-services/User.service';
import Swal from "sweetalert2";

type TutorPacienteVm = {
  idPaciente: string;
  idUsuario: string;
  nombreCompleto: string;
  imagenSrc: string | null;
  initials: string;
};

@Component({
  selector: 'app-patient-health-event-list',
  standalone: true,
  imports: [
    FormsModule,

    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    FeatherIconsComponent,
    MatRippleModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    NgOptimizedImage,
    BreadcrumbComponent,
    TopWidgetsComponent,

    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './patient-health-event-list.component.html',
  styleUrl: './patient-health-event-list.component.scss',
})
export class PatientHealthEventListComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit
{
  displayedColumns = [
    'evento',
    'fecha',
    'descripcion',
    'acciones',
  ];

  data?: PatientHealthEventService;
  dataSource!: PatientHealthEventDataSource;

  idEventoSalud?: string;
  healthEvent?: PatientHealthEvent;

  isPatient: boolean = false;

  /** ===== Tutor UI ===== */
  showTutorSelect = false;
  tutorPacientes: TutorPacienteVm[] = [];
  selectedPacienteId: string | null = null;
  selectedPacienteVm: TutorPacienteVm | null = null;

  /** ✅ paciente logueado (siempre intentamos resolver una vez) */
  private loggedPacienteId: string | null = null;
  private loggedPacienteResolved = false;

  private readonly imageCache = new Map<string, string>();

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild('filter', { static: true }) filter?: ElementRef;

  /** evitar listeners duplicados */
  private filterKeyupSub?: Subscription;

  constructor(
    public httpClient: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    public healthEventService: PatientHealthEventService,
    public authService: AuthManagementService,
    private gfs: GeneralFunctionsService,
    private ces: CreateEditService,
    private cd: ChangeDetectorRef,
    private httpMsgHandler: HttpMesageHandlerService,

    private tutorService: TutorService,
    private userService: UserService
  ) {
    super();

    this.isPatient = this.authService.isUserPatient();
  }

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params['nuevo'] === 'true') {
        this.addNew();
        this.router.navigate([], {
          queryParams: { nuevo: null },
          queryParamsHandling: 'merge',
        }).then();
      }
    });

    // ✅ resolver el paciente logueado (si aplica) una sola vez
    await this.ensureLoggedPacienteId();

    // ✅ si es paciente, intenta cargar pacientes del tutor (si no es tutor, regresará vacío y no muestra select)
    if (this.isPatient) {
      this.tryLoadTutorPacientes();
    }

    this.loadData();
    this.cd.detectChanges();
  }

  refresh() {
    // ✅ respeta la selección actual (token mode vs selectedPacienteId)
    this.loadData();
  }

  // =========================================================
  // ✅ Resuelve loggedPacienteId UNA SOLA VEZ
  // - Paciente normal => tendrá idPaciente
  // - Tutor (rol Paciente pero no es paciente real) => puede quedar null (y está bien)
  // =========================================================
  private async ensureLoggedPacienteId(): Promise<void> {
    if (this.loggedPacienteResolved) return;

    const userData = this.authService.userData?.() ?? (this.authService as any).userData?.();
    const idUsuario = (userData?.IdUsuario ?? userData?.idUsuario ?? '').toString().trim();

    if (!idUsuario) {
      this.loggedPacienteId = null;
      this.loggedPacienteResolved = true;
      return;
    }

    try {
      // este endpoint en tu app ya lo usas en recetas para mapear idUsuario -> idPaciente
      const res: any = await firstValueFrom(this.ces.getpatientByIdUser(idUsuario));
      const payload = res?.data ?? null;
      const row = Array.isArray(payload) ? payload[0] : payload;

      const idPaciente = (row?.idPaciente ?? row?.IdPaciente ?? '').toString().trim();

      this.loggedPacienteId = idPaciente || null;
      this.loggedPacienteResolved = true;
    } catch {
      this.loggedPacienteId = null;
      this.loggedPacienteResolved = true;
    }
  }

  // =========================================================
  // ✅ Para LISTADO: decide si mandamos idPaciente o null
  // - Paciente:
  //   - tutor con selección => selectedPacienteId
  //   - sin selección => null (token mode)
  // - No paciente => null (que el back decida) o ajusta si tu back requiere algo
  // =========================================================
  private resolveIdPacienteForList(): string | null {
    if (!this.isPatient) {
      return null;
    }

    const selected = (this.selectedPacienteId ?? '').toString().trim();
    if (this.showTutorSelect && selected) return selected;

    // token mode
    return null;
  }

  // =========================================================
  // ✅ Paciente objetivo para CREATE/EDIT:
  // - tutor con selección => selectedPacienteId
  // - paciente normal => loggedPacienteId (si existe)
  // - si no hay nada => null (y backend decidirá si permite)
  // =========================================================
  private resolveIdPacienteForWrite(): string | null {
    if (!this.isPatient) return null;

    const selected = (this.selectedPacienteId ?? '').toString().trim();
    if (this.showTutorSelect && selected) return selected;

    return this.loggedPacienteId;
  }

  // =========================================================
  // ✅ DATA SOURCE + SEARCH BOX (sin listeners duplicados)
  // =========================================================
  public loadData() {
    const idPaciente = this.resolveIdPacienteForList();

    this.data = new PatientHealthEventService(this.httpClient, this.gfs, this.httpMsgHandler);

    this.dataSource = new PatientHealthEventDataSource(
      this.data,
      this.paginator,
      this.sort,
      idPaciente
    );

    // limpiar filtro visual
    if (this.filter?.nativeElement) {
      this.filter.nativeElement.value = '';
    }
    this.dataSource.filter = '';

    // evitar duplicados
    this.filterKeyupSub?.unsubscribe();
    this.filterKeyupSub = fromEvent(this.filter?.nativeElement, 'keyup').subscribe(() => {
      if (!this.dataSource) return;
      this.dataSource.filter = this.filter?.nativeElement.value ?? '';
    });

    this.cd.detectChanges();
  }

  // =========================================================
  // ✅ TUTOR: lista de pacientes del tutor
  // =========================================================
  private tryLoadTutorPacientes(): void {
    const userData = this.authService.userData?.() ?? (this.authService as any).userData?.();
    const idTutor = (userData?.IdUsuario ?? userData?.idUsuario ?? '').toString().trim();

    if (!idTutor) {
      this.showTutorSelect = false;
      return;
    }

    this.tutorService.getPacientesByTutor(idTutor).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res ?? [];
        const list = Array.isArray(payload) ? payload : [];

        if (!list.length) {
          this.showTutorSelect = false;
          this.tutorPacientes = [];
          this.selectedPacienteId = null;
          this.selectedPacienteVm = null;
          return;
        }

        this.showTutorSelect = true;

        this.tutorPacientes = list
          .map((row: any) => {
            const idPaciente = String(row?.idPaciente ?? row?.IdPaciente ?? '').trim();
            const idUsuario = String(row?.idUsuario ?? row?.IdUsuario ?? '').trim();

            const n = (row?.nombres ?? row?.Nombres ?? '').toString();
            const a1 = (row?.primerApellido ?? row?.PrimerApellido ?? '').toString();
            const a2 = (row?.segundoApellido ?? row?.SegundoApellido ?? '').toString();

            const nombreCompleto = `${n} ${a1} ${a2}`.replace(/\s+/g, ' ').trim();
            const initials = this.getInitials(nombreCompleto);

            return {
              idPaciente,
              idUsuario,
              nombreCompleto: nombreCompleto || 'PACIENTE',
              imagenSrc: null,
              initials,
            } as TutorPacienteVm;
          })
          .filter((x: TutorPacienteVm) => !!x.idPaciente);

        // NO auto seleccionar
        this.selectedPacienteId = null;
        this.selectedPacienteVm = null;

        // cargar imágenes
        this.tutorPacientes.forEach((p) => this.loadUserImage(p.idUsuario, p.idPaciente));
      },
      error: () => {
        this.showTutorSelect = false;
        this.tutorPacientes = [];
        this.selectedPacienteId = null;
        this.selectedPacienteVm = null;
      },
    });
  }

  onTutorPacienteChange() {
    const idPaciente = (this.selectedPacienteId ?? '').toString().trim();

    if (!idPaciente) {
      this.clearSelectedPaciente();
      return;
    }

    this.selectedPacienteVm = this.tutorPacientes.find((x) => x.idPaciente === idPaciente) ?? null;

    // ✅ recargar listado para ese paciente
    if (this.paginator) this.paginator.firstPage();
    this.loadData();
    this.cd.detectChanges();
  }

  // =========================================================
  // ✅ X: volver a token mode (idPaciente null)
  // =========================================================
  clearSelectedPaciente(): void {
    this.selectedPacienteId = null;
    this.selectedPacienteVm = null;

    if (this.paginator) this.paginator.firstPage();

    this.loadData();
    this.cd.detectChanges();
  }

  // =========================================================
  // IMÁGENES TUTOR (cache)
  // =========================================================
  private loadUserImage(idUsuario: string, idPaciente: string): void {
    if (!idUsuario) return;

    const cached = this.imageCache.get(idUsuario);
    if (cached !== undefined) {
      this.applyImageToVm(idPaciente, cached || null);
      return;
    }

    this.userService.getUserImageBase64(idUsuario).subscribe({
      next: (img: string) => {
        const normalized = (img ?? '').trim();
        const value = normalized ? normalized : '';
        this.imageCache.set(idUsuario, value);
        this.applyImageToVm(idPaciente, value || null);
      },
      error: () => {
        this.imageCache.set(idUsuario, '');
        this.applyImageToVm(idPaciente, null);
      },
    });
  }

  private applyImageToVm(idPaciente: string, img: string | null) {
    const idx = this.tutorPacientes.findIndex((x) => x.idPaciente === idPaciente);
    if (idx < 0) return;

    this.tutorPacientes[idx].imagenSrc = img || null;

    if (this.selectedPacienteVm?.idPaciente === idPaciente) {
      this.selectedPacienteVm = { ...this.tutorPacientes[idx] };
    }

    this.cd.detectChanges();
  }

  private getInitials(name: string): string {
    const parts = (name ?? '').toUpperCase().split(' ').filter(Boolean);
    const a = parts[0]?.[0] ?? 'P';
    const b = parts[1]?.[0] ?? '';
    return (a + b).trim();
  }

  // =========================================================
  // ✅ CRUD UI
  // =========================================================
  addNew() {
    const idPacienteTarget = this.resolveIdPacienteForWrite();

    this.healthEvent = new PatientHealthEvent({
      descripcion: '',
      eventoDeSalud: 0,
      fecha: '',
      idPaciente: idPacienteTarget ?? '',
      nombreEvento: '',
      idEventoSalud: '',
    });

    const dialogRef = this.dialog.open(PatientHealthEventCreateEditComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: {
        entity: this.healthEvent,
        action: 'add',
      },
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        this.data?.dataChange.value.unshift(this.healthEventService.getDialogData());

        setTimeout(() => {
          this.refresh();
          this.refreshTable();
        }, 100);
      }
    });
  }

  editCall(row: PatientHealthEvent) {
    this.idEventoSalud = row.idEventoSalud;

    const dialogRef = this.dialog.open(PatientHealthEventCreateEditComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: {
        entity: row,
        action: 'edit',
      },
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.data?.dataChange.value.findIndex(
          (x) => x.idEventoSalud === this.idEventoSalud
        );
        if (foundIndex != null && this.data) {
          this.data.dataChange.value[foundIndex] = this.healthEventService.getDialogData();
          setTimeout(() => {
            this.refresh();
            this.refreshTable();
          }, 100);
        }
      }
    });
  }

  deleteItem(row: PatientHealthEvent) {
    this.idEventoSalud = row.idEventoSalud;

    const idPacienteToSend =
      (this.showTutorSelect && (this.selectedPacienteId ?? '').toString().trim())
        ? (this.selectedPacienteId as string)
        : (this.loggedPacienteId ?? '');

    if (!this.idEventoSalud || !idPacienteToSend) {
      return;
    }

    Swal.fire({
      title: '¿ELIMINAR EVENTO DE SALUD?',
      text: `¿ESTÁS SEGURO QUE DESEAS ELIMINAR "${row.nombreEvento}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#00A5B8',
      confirmButtonText: 'BORRAR',
      cancelButtonText: 'CANCELAR',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {

        this.healthEventService
          .deletePatientHealthEvent(this.idEventoSalud!, idPacienteToSend);

        const foundIndex = this.data?.dataChange.value.findIndex(
          (x) => x.idEventoSalud === this.idEventoSalud
        );

        if (foundIndex != null && foundIndex > -1 && this.data) {
          this.data.dataChange.value.splice(foundIndex, 1);

          setTimeout(() => {
            this.refresh();
            this.refreshTable();
          }, 150);
        }
      }
    });
  }

  exportExcel() {
    const exportData: Partial<TableElement>[] = this.dataSource.filteredData.map((x: PatientHealthEvent) => ({
      IdPaciente: x.idPaciente,
      Fecha: x.fecha,
      IdEvento: x.eventoDeSalud,
      Descripcion: x.descripcion,
      Nombre: x.nombreEvento,
    }));
    TableExportUtil.exportToExcel(exportData, 'excel');
  }

  private refreshTable() {
    this.paginator._changePageSize(this.paginator.pageSize);
    this.cd.detectChanges();
  }

  // =========================
// ✅ DESCRIPCION: LEER MAS / MENOS
// =========================
  private expandedDesc = new Set<string>();

// Usa un id estable por fila (idEventoSalud normalmente)
  private getRowKey(row: any): string {
    return (row?.idEventoSalud ?? row?.idEvento ?? row?.idPaciente ?? JSON.stringify(row)).toString();
  }

  isDescExpanded(row: any): boolean {
    return this.expandedDesc.has(this.getRowKey(row));
  }

  toggleDesc(row: any): void {
    const key = this.getRowKey(row);
    if (this.expandedDesc.has(key)) this.expandedDesc.delete(key);
    else this.expandedDesc.add(key);
  }

// para decidir si aparece el botón LEER MAS
  shouldShowToggle(desc: string | null | undefined): boolean {
    const text = (desc ?? '').toString().trim();
    return text.length > 200; // ajusta el umbral si quieres
  }
}
